import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import DistList from "../components/DistList";
import TrendBadge from "../components/TrendBadge";
import Sparkline from "../components/Sparkline";
import { fmtNum, statusClass, ragTone, ragLabel } from "../lib/format";
import TrendButton from "../components/TrendButton";

const dsForKpi = (id) => {
  if (id === "total-assets") return "assets";
  if (id === "cs-installed") return "crowdstrike_devices";
  if (id === "hc-alerts") return "alerts";
  if (id === "open-vulns") return "vulnerabilities";
  if (id === "anti-malware") return "crowdstrike_devices?aggressive_amp=true";
  if (id === "unmanaged") return "assets?managed_by_cs=false";
  if (id === "mimecast-gaps") return "email_threats?disposition=Delivered";
  if (id === "unprotected-apps") return "vulnerabilities?domain=Application";
  return "assets";
};

const toneForScore = (score) => ragTone(score);

function DomainCard({ d, onClick }) {
  const tone = ragTone(d.score);
  return (
    <div
      data-testid={`domain-card-${d.id}`}
      className="cv-panel hover:shadow-lg transition-shadow relative"
    >
      <button
        type="button"
        onClick={() => onClick(d.module)}
        className="text-left w-full"
        style={{ background: "transparent", padding: 0, border: 0, cursor: "pointer" }}
      >
        <div className="flex justify-between items-start mb-1">
          <div className="font-bold small-caps text-[13.5px]" style={{ color: "var(--cv-crimson)" }}>
            {d.name}
          </div>
          <TrendBadge trend={d.trend} size="sm" testid={`domain-${d.id}-trend`} />
        </div>
        <div className="flex items-end gap-2">
          <div className="font-serif-display text-[36px] leading-none" style={{ color: tone }}>
            {d.score}
          </div>
          <div className="text-[11.5px] small-caps mb-1" style={{ color: "var(--cv-charcoal-soft)" }}>
            / 100 · target {d.target}
          </div>
        </div>
        <div className="mt-2">
          <Sparkline data={d.sparkline} color={tone} height={36} testid={`domain-${d.id}-spark`} />
        </div>
      </button>
      <div className="mt-2 flex justify-end">
        <TrendButton
          widgetId={`overview-domain-${d.id}`}
          label={`${d.name} · Domain Score`}
          module="overview"
          unit="percent"
          current={d.score}
          testid={`domain-${d.id}-view-trend`}
        />
      </div>
    </div>
  );
}

function RiskRow({ r, onClick }) {
  return (
    <button
      type="button"
      data-testid={`risk-${r.id}`}
      onClick={() => onClick(r.module)}
      className="grid grid-cols-[110px_1fr_180px_140px] gap-3 items-center text-left w-full cv-row-clickable border-b py-3 px-1"
      style={{ borderColor: "var(--cv-line)" }}
    >
      <span className={`status-pill ${statusClass(r.severity)} justify-self-start`}>{r.severity}</span>
      <div>
        <div className="font-bold text-[14.5px]" style={{ color: "var(--cv-charcoal)" }}>{r.title}</div>
        <div className="text-[12px]" style={{ color: "var(--cv-charcoal-soft)" }}>{r.exposure}</div>
      </div>
      <div className="text-[13px]" style={{ color: "var(--cv-charcoal-soft)" }}>{r.domain}</div>
      <div className="text-[13px] font-bold" style={{ color: "var(--cv-crimson)" }}>{r.action} →</div>
    </button>
  );
}

export default function Overview() {
  const [cxo, setCxo] = useState(null);
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    apiGet("/overview/cxo").then(setCxo);
    apiGet("/overview/summary").then(setD);
  }, []);

  if (!cxo || !d) return <div className="p-8 opacity-60">Loading…</div>;

  const compositeTone = ragTone(cxo.composite_score);

  return (
    <>
      <Masthead
        module="overview"
        eyebrow="CXO Briefing · Security Posture"
        title="Overview Dashboard"
        subtitle="Composite posture · top risks · domain health · compliance frameworks · business impact"
        meta={<div>Window: <strong>Last 2 days</strong><br />as at {cxo.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        {/* HERO: Composite Posture */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.9fr] gap-4 mb-5">
          <div className="cv-panel" data-testid="hero-composite" style={{ borderLeft: `4px solid ${compositeTone}` }}>
            <div className="flex justify-between items-start">
              <div className="cv-kpi-label">Overall Security Posture</div>
              <TrendBadge trend={cxo.composite_trend} testid="composite-trend" />
            </div>
            <div className="flex items-end gap-3 mt-3">
              <div className="font-serif-display leading-none" style={{ color: compositeTone, fontSize: 72 }}>
                {cxo.composite_score}
              </div>
              <div className="pb-2">
                <div className="text-[14px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>/ 100</div>
                <div className="text-[12px]" style={{ color: "var(--cv-charcoal-soft)" }}>target {cxo.composite_target}</div>
              </div>
            </div>
            <div className="cv-kpi-sub mt-2">
              Composite of identity, endpoint, network, vulnerability, SOC and email controls — board-confidential snapshot.
            </div>
            <div className="mt-3 flex justify-end">
              <TrendButton
                widgetId="overview-composite-score"
                label="Overall Security Posture"
                module="overview"
                unit="percent"
                current={cxo.composite_score}
                testid="hero-composite-view-trend"
              />
            </div>
          </div>
          <div className="cv-panel" data-testid="hero-spark">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-bold small-caps text-[14.5px]" style={{ color: "var(--cv-charcoal)" }}>
                  12-month posture trajectory
                </div>
                <div className="text-[12.5px] mt-0.5" style={{ color: "var(--cv-charcoal-soft)" }}>
                  Composite score · monthly · improving steadily over the last two quarters
                </div>
              </div>
            </div>
            <div className="mt-3">
              <Sparkline data={cxo.composite_sparkline} color={compositeTone} height={110} testid="composite-spark" />
            </div>
          </div>
        </div>

        {/* DOMAIN HEALTH */}
        <div className="cv-section-label">Domain Health Snapshot</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="domain-health-grid">
          {cxo.domains.map((dom) => (
            <DomainCard key={dom.id} d={dom} onClick={(m) => nav(m)} />
          ))}
        </div>

        {/* TOP RISKS */}
        <div className="cv-section-label">Top Risks Requiring Attention</div>
        <div className="cv-panel mb-5" data-testid="top-risks-panel">
          <div className="grid grid-cols-[110px_1fr_180px_140px] gap-3 text-[11.5px] small-caps font-bold pb-2 border-b"
            style={{ color: "var(--cv-charcoal-soft)", borderColor: "var(--cv-line)" }}>
            <div>Severity</div>
            <div>Risk · Exposure</div>
            <div>Domain</div>
            <div>Recommended Action</div>
          </div>
          {cxo.top_risks.map((r) => <RiskRow key={r.id} r={r} onClick={(m) => nav(m)} />)}
        </div>

        {/* KEY METRICS with trend + sparkline */}
        <div className="cv-section-label">Key Metrics · trend & sparkline</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          {d.kpis.map((k) => (
            <Kpi
              key={k.id}
              testid={`overview-kpi-${k.id}`}
              label={k.label}
              value={k.value}
              sub={k.sub}
              alert={k.tone === "alert"}
              trend={k.trend}
              sparkline={k.sparkline}
              sparkColor={k.tone === "alert" ? "var(--cv-crimson)" : "var(--cv-slate)"}
              onClick={() => nav(`/drill/${dsForKpi(k.id)}`)}
              widgetId={`overview-kpi-${k.id}`}
              module="overview"
              unit={typeof k.value === "string" && k.value.includes("%") ? "percent" : "count"}
              currentNumeric={typeof k.value === "number" ? k.value : undefined}
            />
          ))}
        </div>

        {/* COMPLIANCE + BUSINESS IMPACT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-compliance" title="Compliance Framework Posture" subtitle="Coverage % per framework with quarterly trend"
                 widgetId="overview-compliance-posture" module="overview" unit="percent" current={cxo.compliance?.[0]?.coverage || 0}>
            <div className="flex flex-col gap-3">
              {cxo.compliance.map((c, i) => {
                const tone = ragTone(c.coverage);
                return (
                  <div key={c.framework} data-testid={`compliance-${i}`} className="flex items-center gap-3">
                    <div className="w-44 text-[14px] font-bold" style={{ color: "var(--cv-charcoal)" }}>{c.framework}</div>
                    <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: "var(--cv-line)" }}>
                      <div className="h-full" style={{ width: `${c.coverage}%`, background: tone }} />
                    </div>
                    <div className="w-12 text-right font-serif-display text-[18px] tabular-nums" style={{ color: tone }}>{c.coverage}%</div>
                    <TrendBadge trend={c.trend} size="sm" testid={`compliance-${i}-trend`} />
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel testid="panel-business" title="Business Impact" subtitle="What this posture means in money & hours"
                 widgetId="overview-business-impact" module="overview" unit="currency"
                 current={cxo.business_impact.estimated_annual_exposure_usd}>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => nav("/vuln")} className="cv-tile cv-tile-red text-left" data-testid="bi-exposure">
                <div className="cv-tile-label">Estimated annual exposure</div>
                <div className="cv-tile-val">${(cxo.business_impact.estimated_annual_exposure_usd / 1_000_000).toFixed(1)}M</div>
                <div className="cv-tile-sub">if top risks are not addressed</div>
              </button>
              <button onClick={() => nav("/soc")} className="cv-tile cv-tile-green text-left" data-testid="bi-hours">
                <div className="cv-tile-label">Auto-remediated hours (MTD)</div>
                <div className="cv-tile-val">{cxo.business_impact.auto_remediated_hours_mtd}</div>
                <div className="cv-tile-sub">value delivered by SOAR</div>
              </button>
              <button onClick={() => nav("/soc")} className="cv-tile cv-tile-blue text-left" data-testid="bi-soar">
                <div className="cv-tile-label">SOAR playbook runs (24h)</div>
                <div className="cv-tile-val">{cxo.business_impact.soar_runs_24h}</div>
                <div className="cv-tile-sub">{cxo.business_impact.soar_success_pct}% success rate</div>
              </button>
              <button onClick={() => nav("/soc")} className="cv-tile cv-tile-amber text-left" data-testid="bi-close">
                <div className="cv-tile-label">Mean incident close time</div>
                <div className="cv-tile-val">{cxo.business_impact.mean_close_hrs} hrs</div>
                <div className="cv-tile-sub">+{cxo.business_impact.incidents_closed_wow_delta} closed WoW</div>
              </button>
            </div>
          </Panel>
        </div>

        {/* Existing domain panels (kept for depth) */}
        <div className="cv-section-label">Asset & Endpoint Breakdown</div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel testid="panel-unmanaged" title="Unmanaged Assets" subtitle="Devices discovered by CrowdStrike"
                 widgetId="overview-unmanaged-assets" module="overview" unit="count" current={d.unmanaged.total}>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button onClick={() => nav("/drill/assets?managed_by_cs=false")} className="cv-tile cv-tile-red" data-testid="tile-unmanaged-total">
                <div className="cv-tile-label">Total</div>
                <div className="cv-tile-val">{fmtNum(d.unmanaged.total)}</div>
                <div className="cv-tile-sub">all detected assets</div>
              </button>
              <button onClick={() => nav("/drill/assets?managed_by_cs=false")} className="cv-tile cv-tile-amber" data-testid="tile-unmanaged-high">
                <div className="cv-tile-label">High Confidence</div>
                <div className="cv-tile-val">{fmtNum(d.unmanaged.high_confidence)}</div>
                <div className="cv-tile-sub">score ≥ 75</div>
              </button>
              <button onClick={() => nav("/drill/assets?managed_by_cs=false")} className="cv-tile cv-tile-blue" data-testid="tile-unmanaged-active">
                <div className="cv-tile-label">Active (24h)</div>
                <div className="cv-tile-val">{fmtNum(d.unmanaged.active_24h)}</div>
                <div className="cv-tile-sub">last seen recently</div>
              </button>
            </div>
            <DistList title="Confidence Score Distribution" items={d.unmanaged.confidence_distribution} />
          </Panel>

          <Panel testid="panel-system" title="System Insights" subtitle="Critical system health metrics"
                 widgetId="overview-system-insights" module="overview" unit="count" current={d.system_insights.memory_over_80}>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => nav("/drill/assets")} className="cv-tile cv-tile-red" data-testid="tile-mem">
                <div className="cv-tile-label">Memory &gt; 80%</div>
                <div className="cv-tile-val">{fmtNum(d.system_insights.memory_over_80)}</div>
                <div className="cv-tile-sub">hosts above threshold</div>
              </button>
              <button onClick={() => nav("/drill/assets?encrypted=false")} className="cv-tile cv-tile-red" data-testid="tile-unenc">
                <div className="cv-tile-label">Unencrypted</div>
                <div className="cv-tile-val">{fmtNum(d.system_insights.unencrypted)}</div>
                <div className="cv-tile-sub">missing drive encryption</div>
              </button>
              <button onClick={() => nav("/drill/assets")} className="cv-tile cv-tile-amber" data-testid="tile-disk">
                <div className="cv-tile-label">Disk &gt; 80%</div>
                <div className="cv-tile-val">{fmtNum(d.system_insights.disk_over_80)}</div>
                <div className="cv-tile-sub">high disk usage</div>
              </button>
              <button onClick={() => nav("/drill/assets?eol=true")} className="cv-tile cv-tile-amber" data-testid="tile-eol">
                <div className="cv-tile-label">End of Life</div>
                <div className="cv-tile-val">{fmtNum(d.system_insights.eol_devices)}</div>
                <div className="cv-tile-sub">past vendor EOL</div>
              </button>
            </div>
            <DistList title="Encryption Status Distribution" items={d.system_insights.encryption_distribution} />
          </Panel>

          <Panel testid="panel-inventory" title="Inventory & Discrepancies" subtitle="Asset inventory & data consistency"
                 widgetId="overview-inventory" module="overview" unit="count" current={d.inventory.devices_in_inventory}>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => nav("/drill/assets")} className="cv-tile cv-tile-blue" data-testid="tile-inv-total">
                <div className="cv-tile-label">Devices in Inv.</div>
                <div className="cv-tile-val">{fmtNum(d.inventory.devices_in_inventory)}</div>
                <div className="cv-tile-sub">total devices</div>
              </button>
              <button onClick={() => nav("/drill/assets?managed_by_cs=false")} className="cv-tile cv-tile-amber" data-testid="tile-not-in-cs">
                <div className="cv-tile-label">In Inv, not in CS</div>
                <div className="cv-tile-val">{fmtNum(d.inventory.in_inv_not_cs)}</div>
                <div className="cv-tile-sub">missing from EDR</div>
              </button>
              <button onClick={() => nav("/drill/crowdstrike_devices")} className="cv-tile cv-tile-amber" data-testid="tile-not-in-inv">
                <div className="cv-tile-label">In CS, not in Inv</div>
                <div className="cv-tile-val">{fmtNum(d.inventory.in_cs_not_inv)}</div>
                <div className="cv-tile-sub">missing from inventory</div>
              </button>
              <button onClick={() => nav("/drill/assets")} className="cv-tile cv-tile-green" data-testid="tile-unique-hosts">
                <div className="cv-tile-label">Unique Hostnames</div>
                <div className="cv-tile-val">{fmtNum(d.inventory.unique_hostnames)}</div>
                <div className="cv-tile-sub">inv. + EDR</div>
              </button>
            </div>
            <div className="text-[12px] small-caps font-bold mb-2" style={{ color: "var(--cv-charcoal-soft)" }}>
              RFM Status Distribution
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => nav("/drill/crowdstrike_devices?status=rfm")} className="cv-tile cv-tile-red" data-testid="tile-rfm-ws">
                <div className="cv-tile-label">Workstation</div>
                <div className="cv-tile-val">{d.inventory.rfm_workstation}</div>
              </button>
              <button onClick={() => nav("/drill/crowdstrike_devices?status=rfm")} className="cv-tile cv-tile-red" data-testid="tile-rfm-srv">
                <div className="cv-tile-label">Server</div>
                <div className="cv-tile-val">{d.inventory.rfm_server}</div>
              </button>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
